import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchSettings } from '@/services/settingsService';
import type { LaundryProfile } from '@/types';

interface LaundryProfileContextValue {
  /** Approved profile in effect (name / logo used across the dashboard), or null while loading. */
  profile: LaundryProfile | null;
}

const LaundryProfileContext = createContext<LaundryProfileContextValue>({ profile: null });

/**
 * Shares the approved laundry profile so the sidebar and topbar reflect the
 * approved name/logo once an admin approval is returned by the system (FB10 §6).
 * Falls back to the account data while the system profile is not ready.
 */
export function LaundryProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<LaundryProfile | null>(null);

  useEffect(() => {
    let alive = true;
    fetchSettings()
      .then((data) => {
        if (alive) setProfile(data.profile);
      })
      .catch(() => {
        // keep the account fallback; never block the dashboard on this
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <LaundryProfileContext.Provider value={{ profile }}>{children}</LaundryProfileContext.Provider>
  );
}

export function useLaundryProfile(): LaundryProfileContextValue {
  return useContext(LaundryProfileContext);
}